import { useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import {
	DataTable,
	Badge,
	FilterDropdown,
	TextStack,
} from '../../components/nextgen';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';

import { getSops } from '../../api';

const SOPs = () => {
	const { setOpen: openEditSop } = useModal('editSop');

	// Filters
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
	const [categoryFilter, setCategoryFilter] = useState('all');

	// Data
	const {
		value: sopsData,
		execute: refreshSops,
		status: loadingStatus,
	} = useAsync(getSops);

	useEffect(() => {
		refreshSops();
	}, []);

	// Process SOPs (search and filters)
	const processedSops = useMemo(() => {
		let result = sopsData?.rows || [];

		// Search
		if (search) {
			const q = search.toLowerCase();
			result = result.filter(
				(sop) =>
					sop.name?.toLowerCase().includes(q) ||
					sop.description?.toLowerCase().includes(q),
			);
		}

		// Status filter
		if (statusFilter && statusFilter !== 'all') {
			result = result.filter((sop) => sop.status === statusFilter);
		}

		// Category filter
		if (categoryFilter && categoryFilter !== 'all') {
			if (categoryFilter === 'uncategorized') {
				result = result.filter((sop) => !sop.category);
			} else {
				result = result.filter((sop) => sop.category === categoryFilter);
			}
		}

		return result;
	}, [sopsData, search, statusFilter, categoryFilter]);

	const handleRowClick = (sop) => {
		openEditSop({
			sop,
			onSuccess: () => refreshSops(),
		});
	};

	const isLoading = loadingStatus === 'pending';

	const columns = [
		{
			key: 'status',
			header: 'Status',
			width: '120px',
			render: (sop) => (
				<Badge preset="status" value={sop.status || 'draft'} />
			),
		},
		{
			key: 'name',
			header: 'Name',
			render: (sop) => (
				<TextStack
					primary={sop.name}
					secondary={
						sop?.versionMajor === 0 && sop?.versionMinor === 0
							? 'Unpublished'
							: `Latest: v${sop.versionMajor}.${sop.versionMinor}`
					}
				/>
			),
		},
		{
			key: 'category',
			header: 'Category',
			width: '120px',
			render: (sop) => {
				if (!sop.category) {
					return <span className="text-gray-400 text-sm">—</span>;
				}
				return <Badge preset="category" value={sop.category} />;
			},
		},
		{
			key: 'steps',
			header: 'Steps',
			width: '80px',
			render: (sop) => <Badge variant="muted">{sop.steps?.length || 0}</Badge>,
		},
		{
			key: 'updatedAt',
			header: 'Last Updated',
			width: '140px',
			render: (sop) => (
				<span className="text-sm text-gray-500">
					{moment(sop.updatedAt || sop.createdAt).format('MMM D, YYYY')}
				</span>
			),
		},
	];

	return (
		<MainContent maxWidth={'w-full'}>
			<PageHeading
				actions={[
					{
						name: 'Create SOP',
						onClick: () =>
							openEditSop({
								sop: null,
								onSuccess: () => refreshSops(),
							}),
					},
				]}
				divider={false}
			>
				Standard Operating Procedures
			</PageHeading>

			{/* Search and Filters */}
			<div className="bg-white border border-gray-200 rounded-lg mb-6">
				<div className="flex items-center gap-4 p-4">
					<div className="flex-1 relative">
						<MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search SOPs..."
							className="w-full pl-12 pr-4 py-3 text-sm bg-transparent border-0 focus:outline-none focus:ring-0 placeholder:text-gray-400"
						/>
					</div>
					<FilterDropdown
						label="Status"
						placeholder="All Statuses"
						value={statusFilter}
						options={[
							{ value: 'all', label: 'All Statuses' },
							{ value: 'draft', label: 'Draft' },
							{ value: 'approved', label: 'Approved' },
							{ value: 'deprecated', label: 'Deprecated' },
						]}
						onChange={setStatusFilter}
					/>
					<FilterDropdown
						label="Category"
						placeholder="All Categories"
						value={categoryFilter}
						options={[
							{ value: 'all', label: 'All Categories' },
							{ value: 'uncategorized', label: 'Uncategorized' },
							{ value: 'equipment', label: 'Equipment' },
							{ value: 'process', label: 'Process' },
							{ value: 'safety', label: 'Safety' },
						]}
						onChange={setCategoryFilter}
					/>
				</div>
			</div>

			{/* Table */}
			<DataTable
				data={processedSops}
				columns={columns}
				keyField="id"
				loading={isLoading}
				onRowClick={handleRowClick}
				defaultSort={{ key: 'updatedAt', direction: 'desc' }}
				containerClassName="border border-gray-200 rounded-lg"
				emptyTitle="No SOPs found"
				emptyDescription="Create your first SOP to get started"
				compact
				pagination
				pageSizeOptions={[10, 25, 50]}
			/>
		</MainContent>
	);
};

export default SOPs;
